import styled from 'styled-components';
import { useMemo } from 'react';
import { firefly, paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import {
  Order,
  RfqOrder,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { ActionCell } from '#/components/rfq-common/ActionButtons';
import { RfqActionRow } from '#/components/UnifiedRfqs/rfqs/action/common';
import BboViewProvider from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { Maybe } from '@paradigm/utils/types';
import { renderSecondaryRows, PrimaryOrdersRowExecuted } from '../Orderbook';
import { getOrderExecutedBySorterFn } from './utils';

interface Props {
  readonly executionSide: Side;
  readonly drfq: Drfq;
  readonly bids: readonly RfqOrder[];
  readonly asks: readonly RfqOrder[];
  readonly executedOrder: Maybe<Order>;
}

export default function OrderbookTradeRecap({
  executionSide,
  drfq,
  bids,
  asks,
  executedOrder,
}: Props) {
  const [sortedBids, sortedAsks] = useMemo(() => {
    if (executedOrder == null) return [bids, asks];
    const sortFn = getOrderExecutedBySorterFn(executedOrder.id);

    return [[...bids].sort(sortFn), [...asks].sort(sortFn)];
  }, [asks, bids, executedOrder]);

  const [firstBid, ...remainingBids] = sortedBids;
  const [firstAsk, ...remainingAsks] = sortedAsks;

  const secondaryRows = renderSecondaryRows({
    bids: remainingBids,
    asks: remainingAsks,
    ordersLength: 3,
    rfqId: drfq.id,
  });

  return (
    <BboViewProvider>
      <OrderBook>
        <OrderbookContainer>
          <RfqActionHeader />
          <PrimaryOrdersRowExecuted
            executionSide={executionSide}
            ask={firstAsk}
            bid={firstBid}
            isExpanded
          />
          {secondaryRows}
        </OrderbookContainer>
      </OrderBook>
    </BboViewProvider>
  );
}

function RfqActionHeader() {
  return (
    <RfqActionRowStyled>
      <ActionCell>Qty</ActionCell>
      <ActionCell />
      <ActionCell>Bid</ActionCell>
      <span />
      <ActionCell>Ask</ActionCell>
      <ActionCell />
      <ActionCell>Qty</ActionCell>
    </RfqActionRowStyled>
  );
}

const OrderbookContainer = styled.div`
  background-color: ${firefly};
  border-radius: 3px;
`;

const RfqActionRowStyled = styled(RfqActionRow)`
  background: none;
  height: 32px;
  color: ${paradigmLightBlue};
`;

const OrderBook = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
    align-self: start;
    > * {
      min-width: 0;
    }
  }
`;
